/* Reconnecting WebSocket helper.
 *
 * The chat surface needs:
 *   - Streamed AI tokens (server → client)
 *   - User text and audio chunks (client → server)
 *   - Auto-reconnect with exponential backoff so a flaky network doesn't
 *     require a page reload mid-conversation.
 *
 * We deliberately keep this dependency-free — `reconnecting-websocket` is a
 * 4kb library but pulling it for one use is overkill.
 */

import { resolveWsUrl } from './api';

export type WsState = 'connecting' | 'open' | 'closing' | 'closed';

export interface WsHandlers<TIn> {
  onMessage?: (msg: TIn) => void;
  onState?: (state: WsState) => void;
  onError?: (err: Event) => void;
}

export interface WsHandle<TOut> {
  send: (payload: TOut) => void;
  sendRaw: (payload: ArrayBufferLike | Blob | string) => void;
  close: () => void;
  state: () => WsState;
}

export function createWebSocket<TIn = unknown, TOut = unknown>(
  path: string,
  handlers: WsHandlers<TIn> = {},
): WsHandle<TOut> {
  let socket: WebSocket | null = null;
  let state: WsState = 'closed';
  let closedByUser = false;
  let backoffMs = 500;
  const MAX_BACKOFF = 8000;
  // Buffer messages sent before the socket is open. Reasonable for small
  // chat payloads; we cap so a stuck connection doesn't grow unbounded.
  const pending: (ArrayBufferLike | Blob | string)[] = [];
  const PENDING_CAP = 64;

  const setState = (next: WsState) => {
    state = next;
    handlers.onState?.(next);
  };

  const flush = () => {
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    while (pending.length) {
      const msg = pending.shift();
      if (msg !== undefined) socket.send(msg);
    }
  };

  const connect = () => {
    if (closedByUser) return;
    setState('connecting');
    const url = resolveWsUrl(path);
    socket = new WebSocket(url);

    socket.addEventListener('open', () => {
      backoffMs = 500;
      setState('open');
      flush();
    });

    socket.addEventListener('message', (ev) => {
      if (!handlers.onMessage) return;
      let parsed: unknown;
      if (typeof ev.data === 'string') {
        try {
          parsed = JSON.parse(ev.data);
        } catch {
          parsed = ev.data;
        }
      } else {
        parsed = ev.data;
      }
      handlers.onMessage(parsed as TIn);
    });

    socket.addEventListener('error', (ev) => {
      handlers.onError?.(ev);
    });

    socket.addEventListener('close', () => {
      setState('closed');
      socket = null;
      if (closedByUser) return;
      // Exponential backoff with jitter so multiple reconnects don't herd.
      const jitter = Math.random() * 250;
      const delay = Math.min(backoffMs, MAX_BACKOFF) + jitter;
      backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF);
      setTimeout(connect, delay);
    });
  };

  connect();

  return {
    send: (payload) => {
      const data = JSON.stringify(payload);
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(data);
      } else {
        if (pending.length < PENDING_CAP) pending.push(data);
      }
    },
    sendRaw: (payload) => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(payload);
      } else {
        if (pending.length < PENDING_CAP) pending.push(payload);
      }
    },
    close: () => {
      closedByUser = true;
      setState('closing');
      socket?.close();
    },
    state: () => state,
  };
}
